import {Controller, useForm, useWatch} from 'react-hook-form';
import {useNavigate} from 'react-router-dom';
import {enterPhone} from '@features/auth';
import {yupResolver} from '@hookform/resolvers/yup';
import {Button, Stack, TextField, Typography} from '@mui/material';
import useRequest from 'ahooks/es/useRequest';
import {parsePhoneNumberFromString} from 'libphonenumber-js';
import {object, string} from 'yup';

import {ROUTE__LOGIN__ENTER_CODE} from '@shared/constants';
import {useAuthData} from '../AuthDataContext';
import {DesktopAuthLayout} from '../components/DesktopAuthLayout';

const v8nSchema = object().shape({
  phone: string()
    .required('Enter your phone number')
    .test(
      'international-phone-number',
      'Enter a valid phone number with country code',
      value => {
        const phoneNumber = parsePhoneNumberFromString(value ?? '');

        return Boolean(
          value?.trim().startsWith('+') &&
            phoneNumber?.country &&
            phoneNumber.isValid(),
        );
      },
    ),
});

const getPhoneNumber = (value: string) => parsePhoneNumberFromString(value);

export const EnterPhoneStep = () => {
  const navigate = useNavigate();
  const {
    phone,
    setPhone,
    setTokenToEnterSmsCode,
    setCountryCode: setCountryCodeInContext,
  } = useAuthData();

  const {control, handleSubmit, setError} = useForm({
    mode: 'onBlur',
    resolver: yupResolver(v8nSchema),
    defaultValues: {
      phone,
    },
  });

  const currentPhone = useWatch({control, name: 'phone'});
  const isPhoneValid = Boolean(
    currentPhone?.trim().startsWith('+') &&
      getPhoneNumber(currentPhone)?.country &&
      getPhoneNumber(currentPhone)?.isValid(),
  );

  const {loading, run} = useRequest(enterPhone, {
    manual: true,
    onSuccess: tokenToEnterOtp => {
      setTokenToEnterSmsCode(tokenToEnterOtp);
      navigate(ROUTE__LOGIN__ENTER_CODE);
    },
    onError: () => {
      setError('phone', {
        type: 'custom',
        message: 'Incorrect phone number',
      });
    },
  });

  return (
    <DesktopAuthLayout
      cardProps={{
        onSubmit: event => {
          const theReturnedFunc = handleSubmit(values => {
            const phoneNumber = getPhoneNumber(values.phone);

            if (!phoneNumber?.country || !phoneNumber.isValid()) {
              setError('phone', {
                type: 'custom',
                message: 'Enter a valid phone number with country code',
              });
              return;
            }

            setPhone(phoneNumber.number);
            setCountryCodeInContext(phoneNumber.country);
            run(phoneNumber.number, phoneNumber.country);
          });

          void theReturnedFunc(event);
        },
        noValidate: true,
      }}
    >
      <Stack spacing={3.5} alignItems="center">
        <Typography
          align="center"
          sx={{
            fontWeight: 700,
            fontSize: 32,
            pb: '32px',
          }}
        >
          Log in
        </Typography>

        <Controller
          name="phone"
          control={control}
          render={({field, fieldState}) => (
            <TextField
              {...field}
              value={field.value ?? ''}
              helperText={fieldState.error?.message}
              error={fieldState.invalid}
              fullWidth
              autoFocus
              placeholder="Enter your phone number"
              inputProps={{
                type: 'tel',
                autoComplete: 'tel',
              }}
            />
          )}
        />

        <Button
          type={'submit'}
          color={'primary'}
          loading={loading}
          disabled={!isPhoneValid}
          sx={{width: '100%'}}
        >
          Continue
        </Button>
      </Stack>
    </DesktopAuthLayout>
  );
};
